import { useEffect, useMemo, useState } from 'react';
import { Plus, Tags } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import { Button } from '@ui/button.tsx';
import { Card, CardContent } from '@ui/card.tsx';
import { DataTable } from '@r/components/data-table/data-table.tsx';
import { DataTableSkeleton } from '@r/components/data-table/data-table-skeleton.tsx';
import { DataTableAdvancedToolbar } from '@r/components/data-table/advanced/data-table-advanced-toolbar.tsx';
import { useDataTable } from '@r/hooks/use-data-table.ts';
import type { DataTableFilterField } from '@r/types/index.ts';
import type { ProductBrandProductBrandReadModel } from '@r/api/generated/client.schemas.ts';
import { useUser } from '@r/api/auth.tsx';
import { getActiveWorkspaceUniversal } from '@r/helpers/api.ts';
import { isAdminOrHigher, type Role } from '@r/utils/roles.ts';
import { getAdminTablePersistenceKey } from '@r/utils/data-table-persistence.ts';
import { useOrvalQueryErrorToast } from '@r/hooks/use-orval-error-toast.ts';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle
} from '@ui/dialog.tsx';
import { CreateCustomShopBrandForm } from './create-custom-shop-brand-form.tsx';
import { UpdateCustomShopBrandForm } from './update-custom-shop-brand-form.tsx';
import { useCustomShopBrandColumns } from './custom-shop-brand-columns.tsx';
import {
  apiShopsShopIdbrandsGetCollectionWithFilters,
  buildCustomShopBrandCollectionParams,
  getAdminShopBrandsCatalogQueryKey
} from './custom-shop-brands-api.ts';

interface CustomShopBrandsCatalogProps {
  shopId: number;
}

const INITIAL_COLUMN_VISIBILITY = {
  search: false
};

/** Affiche les marques d'une boutique custom et pilote la modale de création */
export function CustomShopBrandsCatalog({ shopId }: CustomShopBrandsCatalogProps) {
  const { t } = useTranslation();
  const user = useUser();
  const activeWorkspace = getActiveWorkspaceUniversal(user.data?.visibleWorkspaces ?? []);
  const canManageBrands = isAdminOrHigher(activeWorkspace?.maxRole as Role);
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [brandToEdit, setBrandToEdit] = useState<ProductBrandProductBrandReadModel | null>(null);
  const [brands, setBrands] = useState<ProductBrandProductBrandReadModel[]>([]);
  const columns = useCustomShopBrandColumns(setBrandToEdit);

  const filterFields = useMemo<DataTableFilterField<ProductBrandProductBrandReadModel>[]>(
    () => [
      {
        label: t('admin.shops.brands.filter.search'),
        value: 'search',
        placeholder: t('admin.shops.brands.filter.search.placeholder')
      }
    ],
    [t]
  );

  const persistenceKey = useMemo(
    () => getAdminTablePersistenceKey(`shop-${shopId}-brands`, user.data?.id),
    [shopId, user.data?.id]
  );

  const { table } = useDataTable({
    data: brands,
    columns,
    filterFields,
    enableAdvancedFilter: true,
    manualPaginate: false,
    manualSort: false,
    manualFilter: true,
    initialColumnVisibility: INITIAL_COLUMN_VISIBILITY,
    persistence: { key: persistenceKey }
  });

  const { columnFilters } = table.getState();
  const params = useMemo(
    () => buildCustomShopBrandCollectionParams(columnFilters),
    [columnFilters]
  );

  const brandsQuery = useQuery({
    queryKey: getAdminShopBrandsCatalogQueryKey(shopId, params),
    queryFn: ({ signal }) =>
      apiShopsShopIdbrandsGetCollectionWithFilters(String(shopId), params, signal),
    placeholderData: (previousData) => previousData
  });
  useOrvalQueryErrorToast(brandsQuery, t('admin.shops.brands.error.load'));

  useEffect(() => {
    if (!brandsQuery.data) return;
    setBrands(brandsQuery.data);
  }, [brandsQuery.data]);

  return (
    <>
      <Card className="w-full">
        <CardContent className="p-8">
          <div className="flex flex-col gap-6 sm:flex-row sm:items-end sm:justify-between">
            <div className="space-y-2">
              <h2 className="flex gap-3 items-center text-2xl font-bold">
                <Tags className="size-6" />
                {t('admin.shops.brands.title')}
              </h2>
              <p className="text-muted-foreground">{t('admin.shops.brands.description')}</p>
            </div>

            {canManageBrands && (
              <Button type="button" rounded="full" onClick={() => setIsCreateDialogOpen(true)}>
                {t('admin.shops.brands.create.button')}
                <Plus className="size-4" />
              </Button>
            )}
          </div>

          <div className="mt-6">
            <DataTableAdvancedToolbar table={table} filterFields={filterFields} />

            {brandsQuery.isLoading && (
              <DataTableSkeleton
                columnCount={3}
                rowCount={5}
                cellWidths={['4rem', '16rem', '4rem']}
              />
            )}

            {brandsQuery.isSuccess && <DataTable table={table} pagination={false} />}

            {brandsQuery.isError && (
              <div className="flex flex-col gap-4 items-center py-10 text-center rounded-3xl border">
                <p className="text-muted-foreground">{t('admin.shops.brands.error.load')}</p>
                <Button
                  type="button"
                  variant="outline"
                  rounded="full"
                  onClick={() => void brandsQuery.refetch()}
                >
                  {t('admin.shops.catalog.error.retry')}
                </Button>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {canManageBrands && (
        <Dialog open={isCreateDialogOpen} onOpenChange={setIsCreateDialogOpen}>
          <DialogContent className="overflow-hidden p-0 max-w-xl">
            <div className="scrollable max-h-[90vh] overflow-y-auto p-8 md:p-14">
              <DialogHeader>
                <DialogTitle className="text-2xl">
                  {t('admin.shops.brands.create.dialog.title')}
                </DialogTitle>
                <DialogDescription>
                  {t('admin.shops.brands.create.dialog.description')}
                </DialogDescription>
              </DialogHeader>
              <div className="mt-6">
                <CreateCustomShopBrandForm
                  shopId={shopId}
                  onCancel={() => setIsCreateDialogOpen(false)}
                  onSuccess={() => setIsCreateDialogOpen(false)}
                />
              </div>
            </div>
          </DialogContent>
        </Dialog>
      )}

      <Dialog
        open={brandToEdit !== null}
        onOpenChange={(open) => {
          if (!open) setBrandToEdit(null);
        }}
      >
        <DialogContent className="overflow-hidden p-0 max-w-xl">
          <div className="scrollable max-h-[90vh] overflow-y-auto p-8 md:p-14">
            <DialogHeader>
              <DialogTitle className="text-2xl">
                {t('admin.shops.brands.update.dialog.title')}
              </DialogTitle>
              <DialogDescription>
                {t('admin.shops.brands.update.dialog.description')}
              </DialogDescription>
            </DialogHeader>
            {brandToEdit && (
              <div className="mt-6">
                <UpdateCustomShopBrandForm
                  key={brandToEdit.id}
                  shopId={shopId}
                  brand={brandToEdit}
                  onCancel={() => setBrandToEdit(null)}
                  onSuccess={() => setBrandToEdit(null)}
                />
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
